// Shared derivations for material_request_items rows.
// Extracted at Session 74k5 so PurchasingQueuePage.js and RequestsInboxPage.js read one
// implementation instead of each restating item-level logic — the same divergence #12,
// #13, and #215 each cost a full session to collapse after the fact.

export function itemLabel(it) {
  if (!it) return '—';
  return it.is_off_catalog
    ? (it.off_catalog_description || '—')
    : (it.inventory_items?.name || '—');
}

export function itemUom(it) {
  if (!it) return '';
  return it.is_off_catalog ? (it.off_catalog_uom || '') : (it.inventory_items?.uom || '');
}

// Distinct preferred vendors across a set of request items, each with how many of those
// items it is preferred for, most-covered first. Off-catalog rows are skipped - they carry
// no catalog item and therefore no preferred-vendor relationship.
export function preferredVendorPicks(items) {
  const byVendor = new Map();
  (items || []).forEach(it => {
    if (it.is_off_catalog) return;
    const p = it.inventory_items?.vendor_items?.find(vi => vi.is_preferred);
    if (!p?.inventory_vendors) return;
    const existing = byVendor.get(p.inventory_vendors.id);
    if (existing) existing.count += 1;
    else byVendor.set(p.inventory_vendors.id, { vendor: p.inventory_vendors, row: p, count: 1 });
  });
  return Array.from(byVendor.values()).sort((a, b) => b.count - a.count);
}

// Per-item tracking rollup for one request. earliestEta is the minimum ETA across items
// that actually carry a tracking number — untracked items are excluded, so a partially
// tracked request still shows a real earliest arrival rather than nothing.
export function trackingSummary(items) {
  const list = items || [];
  const tracked = list.filter(it => it.tracking_number);
  const etas = tracked.map(it => it.tracking_eta).filter(Boolean);
  const earliestEta = etas.length > 0
    ? etas.reduce((min, e) => (new Date(e) < new Date(min) ? e : min))
    : null;
  return { tracked: tracked.length, total: list.length, earliestEta };
}
// #297, Session 79.14. The label for the tracking pill on a Purchasing Queue card.
//
// WHAT WAS WRONG. The card said "0 of 1 item tracked", and on the On order tab -
// which exists to chase vendors - a buyer read that as a DELIVERY status. It is
// not one. It counts how many lines have had a carrier and a tracking number
// typed in, and says nothing whatsoever about whether anything has arrived.
//
// The word doing the damage was "tracked", which sounds like a state a shipment
// is in. Saying "has a tracking number" says what is actually being counted, and
// nothing else about the card changes.
//
// THIS IS A FUNCTION RATHER THAN THREE STRINGS BECAUSE THE OLD LABEL WAS WRITTEN
// OUT THREE TIMES - the desktop card, the desktop detail panel and the mobile
// detail sheet. #358 is the precedent: a defect found in one place turned out to
// render in three, and fixing the one you happened to open leaves the other two.
export function trackingLabel(track) {
  const tracked = track?.tracked ?? 0;
  const total = track?.total ?? 0;
  if (total === 0) return 'No items';
  if (tracked === 0) {
    return total === 1
      ? 'No tracking number yet'
      : 'No tracking numbers yet';
  }
  // #705, Session 79.22b.4.6.1c -- THE NOUN FOLLOWS THE ITEMS, THE VERB FOLLOWS
  // THE ONES COUNTED. "1 of 3 items have tracking numbers" was #297's own label:
  // "items" is right (there are three), but the subject of the verb is the 1, so
  // it reads "1 of 3 items has a tracking number". #345's lesson still holds for
  // the noun -- it is never built from whatever number is nearest.
  return total === 1
    ? `${tracked} of ${total} item has a tracking number`
    : `${tracked} of ${total} items ${tracked === 1 ? 'has a tracking number' : 'have tracking numbers'}`;
}
